<template>
	<CampaignLayout :subtitle="trans('tools.campaigns.send.enrollees')" :campaign="campaign">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="send">
				<i class="fa-solid fa-paper-plane mr-2"/>
				{{ trans('general.send') }}
			</PrimaryButton>
		</template>
		<div class="flex justify-start items-end gap-3">
			<LabeledObject>
				<template #label>{{ trans('general.event') }}</template>
				<select v-model="searchParameters.event_type" @change.stop="searchEvents">
					<option v-for="eventType in EventType" :value="eventType">{{ trans(`events.${eventType}.title`) }}</option>
				</select>
			</LabeledObject>
		</div>
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
			<Card :title="trans('general.fetched.title')" no-grow>
				<List v-if="selectable.length > 0">
					<ListItem v-for="item in selectable" :key="`fetched-events-${item.id}`" class="cursor-pointer" :title="item.title" @click.stop="add(item)">
						<i class="fa-solid fa-chevron-right text-gray-500"/>
					</ListItem>
				</List>
				<p v-else>{{ trans('general.empty') }}</p>
			</Card>
			<Card :title="trans('general.selected.title')" no-grow>
				<List v-if="selectedEvent">
					<ListItem :title="selectedEvent.title">
						<SoftButton :color="Color.Red" @click.stop="remove">
							<i class="fa-solid fa-trash mr-2"/>
							{{ trans('general.delete') }}
						</SoftButton>
					</ListItem>
				</List>
				<p v-else>{{ trans('general.empty') }}</p>
			</Card>
		</div>
	</CampaignLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import {type Event} from '@/Types/Events/Event'
import {EventType} from '@/Types/Enums/EventType'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import CampaignLayout from '@/Layouts/CampaignLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type Campaign} from '@/Types/Tools/Campaigns/Campaign'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {computed, type ComputedRef, defineProps, type Ref, ref} from 'vue'
import {showInfo, showSuccess, showValidationErrors, showWarning} from '@/Helpers/general'

interface SearchParameters {
	event_type:string,
	fetched:Array<Event>,
}

const props = defineProps<{
	campaign:Campaign,
}>()

const selectable:ComputedRef<Array<Event>> = computed(():Array<Event> => {
	return searchParameters.value.fetched.filter((item:Event) => selectedEvent.value?.id !== item.id)
})

const reloads:Ref<number> = ref(0)
const working:Ref<boolean> = ref(false)
const campaign:Ref<Campaign> = ref(props.campaign)
const selectedEvent:Ref<Event|null> = ref(null)
const searchParameters:Ref<SearchParameters> = ref({
	event_type: '',
	fetched: [],
})

function add(event:Event):void{
	selectedEvent.value = event
}

function remove():void{
	selectedEvent.value = null
}

function searchEvents():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.searching'))
		axios.post(route('search.index', 'events'), {
			event_type: searchParameters.value.event_type,
		}).then(({data}:{data:Array<Event>}) => {
			showSuccess(trans('general.shown'))
			searchParameters.value.fetched = data.reverse()
			reloads.value++
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function send():void{
	if(selectedEvent.value === null){
		showWarning(trans('general.selected.empty'))
	} else if(!working.value){
		working.value = true
		showInfo(trans('tools.campaigns.mails.sending'))
		axios.post(route('tools.campaigns.send.enrollees.store', campaign.value.id), {
			event_id: selectedEvent.value.id,
		}).then(() => {
			showSuccess(trans('tools.campaigns.mails.sent'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>
